import type { Feature, FeatureCollection, MultiPolygon, Polygon } from "geojson";
import L from "leaflet";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Circle, CircleMarker, FeatureGroup, GeoJSON, MapContainer, Polyline, Tooltip, useMap } from "react-leaflet";
import { IRONFLEET_BOOTH } from "../constants";
import {
  buildWalkGraph,
  cadPathToLatLngs,
  findBoothNodeId,
  nearestNodeId,
  shortestWalk,
} from "../geo/aisleRoute";
import { getGpsFix, isNativeGeolocation, watchGps, type GpsFix } from "../geo/geolocation";
import {
  clearGpsLock,
  createLockFromFix,
  featureCentroid,
  gpsToCad,
  DEFAULT_CAD_UNITS_PER_METER,
  IRONFLEET_BOOTH_LAT,
  IRONFLEET_BOOTH_LNG,
  type CadPoint,
  type GpsCadLock,
} from "../geo/gpsCadOffset";
import { WalkIcon } from "./BoothNavButton";
import { StatusBlock } from "./StatusBlock";
import { TeamBrowser } from "../floor/TeamBrowser";
import { boothTooltipLabel } from "../floor/boothOccupancy";
import { normalizeBoothId } from "../floor/boothLookup";
import { teamColor, teamMemberFromPresence, type TeamColor, type TeamMember } from "../floor/teamColors";
import type { AisleGraph, HallBoothCollection, HallBoothProps, LoadState, PresenceRecord } from "../types";
import "leaflet/dist/leaflet.css";

const HALLS = ["A", "C"] as const;
const LOCATE_ZOOM = 1.6;
const GPS_ACCURACY_SCALE = 0.4;
const DEFAULT_GPS_ACCURACY_M = 15;

function gpsAccuracyRadiusCad(accuracyM: number | null | undefined, unitsPerMeter: number): number {
  const meters = accuracyM != null && accuracyM > 0 ? accuracyM : DEFAULT_GPS_ACCURACY_M;
  return meters * unitsPerMeter * GPS_ACCURACY_SCALE;
}

function GpsAccuracyHalo({
  center,
  accuracyM,
  unitsPerMeter,
  color,
}: {
  center: [number, number];
  accuracyM?: number | null;
  unitsPerMeter: number;
  color: TeamColor;
}) {
  return (
    <Circle
      center={center}
      radius={gpsAccuracyRadiusCad(accuracyM, unitsPerMeter)}
      interactive={false}
      bubblingMouseEvents={false}
      className="gps-accuracy"
      pathOptions={{
        color: color.stroke,
        weight: 1,
        fillColor: color.fill,
        fillOpacity: 0.12,
        className: "gps-accuracy",
      }}
    />
  );
}

type HallMapProps = {
  hallId: string;
  booths: HallBoothCollection | null;
  aisles: AisleGraph | null;
  geoState?: LoadState<HallBoothCollection>;
  selectedBooth?: string;
  originBooth?: string | null;
  destinationBooth?: string | null;
  tourPath?: CadPoint[] | null;
  tourBoothIds?: ReadonlySet<string>;
  highlightedBoothIds?: ReadonlySet<string>;
  sharedBoothIds?: ReadonlySet<string>;
  highlightPending?: boolean;
  presence?: PresenceRecord[];
  selfUserId?: string;
  selfEmail?: string;
  selfOnline?: boolean;
  onHallChange: (hall: string) => void;
  onSelectBooth: (boothId: string) => void;
  onGpsPin?: (point: CadPoint | null, geo?: GpsFix | null) => void;
  onClearRoute?: () => void;
  onOpenMember?: (member: TeamMember) => void;
  onOpenDrawer?: () => void;
  filtersActive?: boolean;
  planSummary?: string;
};


function collectionBounds(collection: FeatureCollection | null): L.LatLngBounds | null {
  if (!collection?.features.length) return null;
  const layer = L.geoJSON(collection);
  const bounds = layer.getBounds();
  return bounds.isValid() ? bounds : null;
}

function ironfleetCad(booths: HallBoothCollection | null): CadPoint | null {
  const match = booths?.features.find((feature) => feature.properties.booth_id === IRONFLEET_BOOTH);
  if (!match?.geometry || (match.geometry.type !== "Polygon" && match.geometry.type !== "MultiPolygon")) {
    return null;
  }
  return featureCentroid(match.geometry);
}

function FitHall({
  bounds,
  onOverviewZoom,
}: {
  bounds: L.LatLngBounds | null;
  onOverviewZoom?: (zoom: number) => void;
}) {
  const map = useMap();
  useEffect(() => {
    if (!bounds) return;
    map.fitBounds(bounds, { padding: [20, 20] });
    onOverviewZoom?.(map.getZoom());
  }, [map, bounds, onOverviewZoom]);
  return null;
}

/** Leaflet hit-testing stays stale after a programmatic zoom/resize until the view is reset. */
function refreshMapInteraction(map: L.Map) {
  map.invalidateSize({ animate: false });
  map.setView(map.getCenter(), map.getZoom(), { animate: false });
}

function FitWalk({
  destinationBooth,
  positions,
}: {
  destinationBooth?: string | null;
  positions: Array<[number, number]>;
}) {
  const map = useMap();
  const key = `${destinationBooth ?? ""}:${positions.length ? `${positions[0].join(",")}:${positions[positions.length - 1].join(",")}:${positions.length}` : ""}`;
  useEffect(() => {
    if (positions.length < 2 && !destinationBooth) return;
    let cancelled = false;
    let timeout = 0;
    const restore = () => {
      if (cancelled) return;
      refreshMapInteraction(map);
    };
    const run = () => {
      if (cancelled) return;
      map.invalidateSize({ animate: false });
      if (positions.length >= 2) {
        const bounds = L.latLngBounds(positions);
        if (bounds.isValid()) {
          map.once("moveend", restore);
          map.fitBounds(bounds, { padding: [36, 36], maxZoom: 1.25 });
        }
      }
      timeout = window.setTimeout(restore, 420);
    };
    // Let the drawer close / modal unlock finish laying out first.
    timeout = window.setTimeout(run, 0);
    return () => {
      cancelled = true;
      map.off("moveend", restore);
      window.clearTimeout(timeout);
    };
  }, [map, key, destinationBooth, positions]);
  return null;
}

function MapSizeSync({ positions }: { positions: Array<[number, number]> }) {
  const map = useMap();
  const positionsRef = useRef(positions);
  positionsRef.current = positions;
  useEffect(() => {
    const container = map.getContainer();
    let timeout = 0;
    let lastWidth = 0;
    let lastHeight = 0;
    const observer = new ResizeObserver(() => {
      const width = container.clientWidth;
      const height = container.clientHeight;
      if (width === lastWidth && height === lastHeight) return;
      lastWidth = width;
      lastHeight = height;
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => {
        map.invalidateSize({ animate: false });
        const next = positionsRef.current;
        if (next.length >= 2) {
          const bounds = L.latLngBounds(next);
          if (bounds.isValid()) {
            map.fitBounds(bounds, { padding: [36, 36], maxZoom: 1.25, animate: false });
          }
        }
        refreshMapInteraction(map);
      }, 60);
    });
    observer.observe(container);
    return () => {
      window.clearTimeout(timeout);
      observer.disconnect();
    };
  }, [map]);
  return null;
}

function FocusBooth({
  booths,
  selectedBooth,
  skip,
}: {
  booths: HallBoothCollection | null;
  selectedBooth?: string;
  skip?: boolean;
}) {
  const map = useMap();
  useEffect(() => {
    if (skip || !booths || !selectedBooth) return;
    const match = booths.features.find((feature) => feature.properties.booth_id === selectedBooth);
    if (!match?.geometry) return;
    const bounds = L.geoJSON(match).getBounds();
    if (!bounds.isValid()) return;
    map.panTo(bounds.getCenter(), { animate: true });
  }, [map, booths, selectedBooth, skip]);
  return null;
}

function MapBinder({ onMap }: { onMap: (map: L.Map) => void }) {
  const map = useMap();
  useEffect(() => {
    onMap(map);
  }, [map, onMap]);
  return null;
}

function boothStyle(kind: "default" | "selected" | "destination" | "match" | "match-pending"): L.PathOptions {
  if (kind === "selected") {
    return { color: "#f2af74", weight: 2.4, fillColor: "#e8873a", fillOpacity: 0.88, className: "booth-poly booth-selected" };
  }
  if (kind === "destination") {
    return {
      color: "#4abaf0",
      weight: 2.4,
      fillColor: "#1d5f80",
      fillOpacity: 0.82,
      dashArray: "4 3",
      className: "booth-poly booth-destination",
    };
  }
  if (kind === "match-pending") {
    return {
      color: "#f2af74",
      weight: 3,
      fillColor: "#e8873a",
      fillOpacity: 0.5,
      dashArray: "5 4",
      className: "booth-poly booth-match-pending",
    };
  }
  if (kind === "match") {
    return { color: "#f2af74", weight: 3, fillColor: "#e8873a", fillOpacity: 0.8, className: "booth-poly booth-match" };
  }
  return { color: "#9aa3ab", weight: 1, fillColor: "#1d3142", fillOpacity: 0.35, className: "booth-poly" };
}

function featureBoothId(feature?: Feature): string {
  const props = feature?.properties as HallBoothProps | null | undefined;
  return normalizeBoothId(props?.booth_id);
}

function boothOnHall(booths: HallBoothCollection | null | undefined, booth?: string | null): boolean {
  const key = normalizeBoothId(booth);
  if (!key || !booths?.features.length) return false;
  return booths.features.some((feature) => featureBoothId(feature) === key);
}

function edgeStyle(feature?: Feature): L.PathOptions {
  const kind = String(feature?.properties?.kind ?? "secondary");
  const passive: L.PathOptions = { interactive: false, bubblingMouseEvents: false };
  if (kind === "primary") {
    return { ...passive, color: "#e8873a", weight: 2.6, opacity: 0.95 };
  }
  if (kind === "entrance") {
    return { ...passive, color: "#4eaad9", weight: 2.2, opacity: 0.9 };
  }
  if (kind === "portal") {
    return { ...passive, color: "#6fbf88", weight: 1, opacity: 0.35 };
  }
  return { ...passive, color: "#7b8085", weight: 1.15, opacity: 0.7 };
}

const WALK_ROUTE_STYLE: L.PathOptions = {
  color: "#4ecdc4",
  weight: 5,
  opacity: 0.95,
  lineJoin: "round",
  lineCap: "round",
  interactive: false,
  bubblingMouseEvents: false,
};

function entrancePoints(nodes: FeatureCollection | undefined): Array<{
  id: string;
  x: number;
  y: number;
  label: string;
}> {
  if (!nodes) return [];
  const out: Array<{ id: string; x: number; y: number; label: string }> = [];
  for (const feature of nodes.features) {
    if (feature.properties?.kind !== "entrance") continue;
    if (feature.geometry?.type !== "Point") continue;
    const [x, y] = feature.geometry.coordinates;
    out.push({
      id: String(feature.properties.node_id ?? `${x},${y}`),
      x,
      y,
      label: String(feature.properties.label ?? "Entrance"),
    });
  }
  return out;
}

function presenceLocator(
  entry: PresenceRecord,
  hallId: string,
  hallBounds: L.LatLngBounds | null,
): { x: number; y: number; kind: "gps" | "booth" } | null {
  if (entry.gps_on !== false && entry.gps_xy && hallBounds?.contains([entry.gps_xy[1], entry.gps_xy[0]])) {
    return { x: entry.gps_xy[0], y: entry.gps_xy[1], kind: "gps" };
  }
  if (entry.hall_id === hallId && entry.floor_xy) {
    return { x: entry.floor_xy[0], y: entry.floor_xy[1], kind: "booth" };
  }
  return null;
}

export function HallMap({
  hallId,
  booths,
  aisles,
  geoState,
  selectedBooth,
  originBooth,
  destinationBooth,
  tourPath,
  tourBoothIds,
  highlightedBoothIds,
  sharedBoothIds,
  highlightPending = false,
  presence = [],
  selfUserId,
  selfEmail,
  selfOnline = false,
  onHallChange,
  onSelectBooth,
  onGpsPin,
  onClearRoute,
  onOpenMember,
  onOpenDrawer,
  filtersActive = false,
  planSummary,
}: HallMapProps) {
  const drawnBooths = booths;
  const highlightKeys = useMemo(() => {
    if (!highlightedBoothIds?.size) return undefined;
    const keys = new Set<string>();
    for (const id of highlightedBoothIds) {
      const key = normalizeBoothId(id);
      if (key) keys.add(key);
    }
    return keys.size ? keys : undefined;
  }, [highlightedBoothIds]);
  const matchCount = useMemo(() => {
    if (!booths || !highlightKeys?.size) return 0;
    let count = 0;
    for (const feature of booths.features) {
      if (highlightKeys.has(featureBoothId(feature))) count += 1;
    }
    return count;
  }, [booths, highlightKeys]);
  const hallBounds = useMemo(() => collectionBounds(booths), [booths]);
  const bounds = hallBounds;
  const boothLayerKey = `${hallId}-${highlightPending ? "pending" : "ready"}-${highlightKeys ? [...highlightKeys].sort().join(",") : "none"}`;
  const selectedFeature = useMemo(
    () =>
      drawnBooths?.features.find((feature) => feature.properties.booth_id === selectedBooth) ?? null,
    [drawnBooths, selectedBooth],
  );
  const destinationKeys = useMemo(() => {
    const keys = new Set<string>();
    const dest = normalizeBoothId(destinationBooth);
    if (dest) keys.add(dest);
    if (tourBoothIds?.size) {
      for (const id of tourBoothIds) {
        const key = normalizeBoothId(id);
        if (key) keys.add(key);
      }
    }
    return keys.size ? keys : undefined;
  }, [destinationBooth, tourBoothIds]);
  const destinationCollection = useMemo<HallBoothCollection | null>(() => {
    if (!destinationKeys?.size || !booths?.features.length) return null;
    const features = booths.features.filter((feature) => destinationKeys.has(featureBoothId(feature)));
    return features.length ? { type: "FeatureCollection", features } : null;
  }, [booths, destinationKeys]);
  const walkEdges = useMemo(() => {
    if (!aisles?.edges) return null;
    return {
      type: "FeatureCollection" as const,
      features: aisles.edges.features.filter((feature) => feature.properties?.kind !== "portal"),
    };
  }, [aisles]);
  const selectedCollection = useMemo<HallBoothCollection | null>(
    () => (selectedFeature ? { type: "FeatureCollection", features: [selectedFeature] } : null),
    [selectedFeature],
  );
  const hallPresence = useMemo(
    () => presence.filter((entry) => entry.booth_id || entry.gps_xy || entry.destination_booth_id),
    [presence],
  );
  const [showTeamGps, setShowTeamGps] = useState(true);

  const alignButtonRef = useRef<HTMLButtonElement>(null);
  const overlayPinsRef = useRef<L.FeatureGroup | null>(null);
  const watchRef = useRef<{ stop: () => void } | null>(null);
  const [leafletMap, setLeafletMap] = useState<L.Map | null>(null);
  const [overviewZoom, setOverviewZoom] = useState<number | null>(null);
  const [origin, setOrigin] = useState<GpsCadLock | null>(null);
  const [gpsOn, setGpsOn] = useState(isNativeGeolocation);
  const [fix, setFix] = useState<GpsFix | null>(null);
  const [gpsMessage, setGpsMessage] = useState<string | null>(null);
  const [gpsBusy, setGpsBusy] = useState(false);
  const [highlightAlign, setHighlightAlign] = useState(false);

  const onMapReady = useCallback((map: L.Map) => {
    setLeafletMap(map);
  }, []);

  const stopWatch = useCallback(() => {
    watchRef.current?.stop();
    watchRef.current = null;
  }, []);

  const startWatch = useCallback(() => {
    if (watchRef.current) return;
    watchRef.current = watchGps(
      (next) => {
        setFix(next);
        setGpsMessage(null);
      },
      (message) => setGpsMessage(message),
    );
  }, []);

  useEffect(() => {
    setLeafletMap(null);
    setOverviewZoom(null);
  }, [hallId]);

  const boothOrigin = useMemo(() => {
    const cad = ironfleetCad(booths);
    if (!cad) return null;
    return createLockFromFix(IRONFLEET_BOOTH_LAT, IRONFLEET_BOOTH_LNG, cad);
  }, [booths]);

  useEffect(() => {
    clearGpsLock();
  }, []);

  useEffect(() => {
    if (boothOrigin) setOrigin(boothOrigin);
  }, [boothOrigin]);

  useEffect(() => {
    if (!gpsOn) {
      stopWatch();
      return;
    }
    startWatch();
    return () => stopWatch();
  }, [gpsOn, startWatch, stopWatch]);

  const cadPin = useMemo(() => {
    if (!gpsOn || !origin || !fix) return null;
    return gpsToCad(fix.lat, fix.lng, origin);
  }, [gpsOn, origin, fix]);
  const pinOnHall = Boolean(cadPin && hallBounds?.contains([cadPin.y, cadPin.x]));
  const lastGpsSent = useRef<{ t: number; x: number; y: number } | null>(null);

  useEffect(() => {
    if (!onGpsPin) return;
    if (!gpsOn || !origin) {
      if (lastGpsSent.current) {
        lastGpsSent.current = null;
        onGpsPin(null);
      }
      return;
    }
    if (!cadPin) return;
    const now = Date.now();
    const prev = lastGpsSent.current;
    const moved = !prev || Math.hypot(cadPin.x - prev.x, cadPin.y - prev.y) > 40;
    const aged = !prev || now - prev.t >= 5000;
    if (!moved && !aged) return;
    lastGpsSent.current = { t: now, x: cadPin.x, y: cadPin.y };
    onGpsPin(cadPin, fix);
  }, [gpsOn, origin, cadPin, fix, onGpsPin]);

  const walkGraph = useMemo(() => buildWalkGraph(aisles), [aisles]);
  const walkPath = useMemo(() => {
    if (tourPath && tourPath.length >= 2) return tourPath;
    if (!walkGraph || !destinationBooth) return null;
    if (!boothOnHall(booths, destinationBooth)) return null;
    const goal = findBoothNodeId(walkGraph, destinationBooth);
    if (!goal) return null;
    const originOnThisHall = boothOnHall(booths, originBooth);
    const start = pinOnHall && cadPin
      ? nearestNodeId(walkGraph, cadPin.x, cadPin.y)
      : originOnThisHall && originBooth
        ? findBoothNodeId(walkGraph, originBooth)
        : null;
    if (!start) return null;
    const path = shortestWalk(walkGraph, start, goal);
    return path && path.length >= 2 ? path : null;
  }, [walkGraph, destinationBooth, originBooth, cadPin, pinOnHall, tourPath, booths]);
  const walkLatLngs = useMemo(() => (walkPath ? cadPathToLatLngs(walkPath) : []), [walkPath]);

  const onEachBooth = useCallback(
    (feature: Feature<PolygonOrMulti, HallBoothProps>, layer: L.Layer) => {
      const id = feature.properties.booth_id;
      const name = feature.properties.name;
      layer.bindTooltip(boothTooltipLabel(id, name, Boolean(sharedBoothIds?.has(id))), { sticky: true, opacity: 0.95 });
      layer.on("click", () => onSelectBooth(id));
      layer.on("mouseover", () => {
        if (layer instanceof L.Path) layer.bringToFront();
      });
      layer.on("mouseout", () => {
        overlayPinsRef.current?.bringToFront();
      });
    },
    [onSelectBooth, sharedBoothIds],
  );

  const handleGpsToggle = async () => {
    setHighlightAlign(false);
    if (gpsOn) {
      setGpsOn(false);
      setFix(null);
      setGpsMessage(null);
      return;
    }
    if (!origin) {
      setGpsMessage(`Booth ${IRONFLEET_BOOTH} is not on this hall — switch to Hall A.`);
      return;
    }
    setGpsOn(true);
    setGpsBusy(true);
    try {
      const nextFix = await getGpsFix();
      setFix(nextFix);
      setGpsMessage(null);
    } catch (error) {
      setGpsMessage(error instanceof Error ? error.message : "Location unavailable");
    } finally {
      setGpsBusy(false);
    }
  };

  const handleLocate = async () => {
    if (!origin) {
      setHighlightAlign(true);
      alignButtonRef.current?.focus();
      setGpsMessage(`Booth ${IRONFLEET_BOOTH} is not on this hall — switch to Hall A.`);
      return;
    }
    if (!gpsOn) setGpsOn(true);
    setGpsBusy(true);
    try {
      let nextCad = cadPin;
      if (!nextCad) {
        const nextFix = await getGpsFix();
        setFix(nextFix);
        nextCad = gpsToCad(nextFix.lat, nextFix.lng, origin);
      }
      if (!nextCad || !leafletMap) return;
      if (bounds && !bounds.contains([nextCad.y, nextCad.x])) {
        setGpsMessage("Your location is not on this hall — switch to Hall A.");
        return;
      }
      const zoom = leafletMap.getZoom();
      const atOverview = overviewZoom != null && zoom <= overviewZoom + 0.05;
      if (atOverview) {
        leafletMap.setView([nextCad.y, nextCad.x], LOCATE_ZOOM, { animate: true });
      } else {
        leafletMap.panTo([nextCad.y, nextCad.x], { animate: true });
      }
    } catch (error) {
      setGpsMessage(error instanceof Error ? error.message : "Location unavailable");
    } finally {
      setGpsBusy(false);
    }
  };

  const gpsStatus = gpsBusy
    ? "GPS locating…"
    : gpsOn
      ? gpsMessage
        ? `GPS on · ${gpsMessage}`
        : `GPS on · booth ${IRONFLEET_BOOTH}`
      : gpsMessage
        ? gpsMessage
        : "GPS off";
  const selfMember = teamMemberFromPresence({
    email: selfEmail,
    display_name: presence.find((entry) => entry.user_id === selfUserId)?.display_name,
    user_id: selfUserId,
  });
  const selfColor = selfMember?.color ?? teamColor(selfEmail || selfUserId || "self");

  return (
    <div className="hall-map-shell">
      <div className="hall-map">
        {geoState && geoState.status !== "ready" ? (
          <StatusBlock state={geoState} loadingLabel={`Loading Hall ${hallId} geometry…`} />
        ) : booths && hallBounds ? (
          <>
            <MapContainer
              key={hallId}
              crs={L.CRS.Simple}
              center={hallBounds.getCenter()}
              zoom={-2}
              minZoom={-5}
              maxZoom={3}
              zoomSnap={0.25}
              attributionControl={false}
              zoomControl={false}
              className="hall-map-leaflet"
            >
              <MapBinder onMap={onMapReady} />
              <FitHall bounds={bounds} onOverviewZoom={setOverviewZoom} />
              <MapSizeSync positions={walkLatLngs} />
              <FocusBooth booths={drawnBooths} selectedBooth={destinationBooth || selectedBooth} skip={walkLatLngs.length >= 2} />
              <FitWalk destinationBooth={destinationBooth} positions={walkLatLngs} />
              {drawnBooths && drawnBooths.features.length ? (
                <GeoJSON
                  key={boothLayerKey}
                  data={drawnBooths}
                  style={(feature) => {
                    const id = featureBoothId(feature);
                    if (id && highlightKeys?.has(id)) {
                      return boothStyle(highlightPending ? "match-pending" : "match");
                    }
                    return boothStyle("default");
                  }}
                  onEachFeature={onEachBooth}
                />
              ) : null}
              {walkEdges ? (
                <GeoJSON
                  key={`aisles-${hallId}-${walkEdges.features.length}`}
                  data={walkEdges}
                  style={edgeStyle}
                  interactive={false}
                />
              ) : null}
              {selectedCollection && !destinationKeys?.has(normalizeBoothId(selectedBooth)) ? (
                <GeoJSON
                  key={`selected-${selectedBooth ?? ""}`}
                  data={selectedCollection}
                  style={() => boothStyle("selected")}
                  onEachFeature={onEachBooth}
                />
              ) : null}
              {walkLatLngs.length >= 2 ? (
                <Polyline positions={walkLatLngs} pathOptions={WALK_ROUTE_STYLE} interactive={false} />
              ) : null}
              {destinationCollection ? (
                <GeoJSON
                  key={`destination-${destinationBooth ?? ""}-${destinationKeys ? [...destinationKeys].sort().join(",") : "none"}`}
                  data={destinationCollection}
                  style={() => boothStyle("destination")}
                  onEachFeature={onEachBooth}
                  eventHandlers={{
                    add: (event) => {
                      event.target.bringToFront();
                    },
                  }}
                />
              ) : null}
              {entrancePoints(aisles?.nodes).map((entrance) => (
                <CircleMarker
                  key={entrance.id}
                  center={[entrance.y, entrance.x]}
                  radius={7}
                  pathOptions={{ color: "#1d5f80", fillColor: "#4eaad9", fillOpacity: 0.95, weight: 2 }}
                >
                  <Tooltip permanent direction="bottom" offset={[0, 8]}>
                    {entrance.label}
                  </Tooltip>
                </CircleMarker>
              ))}
              <FeatureGroup ref={overlayPinsRef}>
                {hallPresence.map((entry) => {
                  const isSelf = entry.user_id === selfUserId;
                  if (isSelf && pinOnHall) return null;
                  if (!isSelf && !showTeamGps) return null;
                  const locator = presenceLocator(entry, hallId, hallBounds);
                  if (!locator) return null;
                  const color = teamMemberFromPresence(entry)?.color ?? teamColor(entry.email || entry.user_id);
                  const unitsPerMeter = origin?.unitsPerMeter ?? DEFAULT_CAD_UNITS_PER_METER;
                  return (
                    <Fragment key={entry.user_id}>
                      {locator.kind === "gps" ? (
                        <GpsAccuracyHalo
                          center={[locator.y, locator.x]}
                          accuracyM={entry.gps_accuracy_m}
                          unitsPerMeter={unitsPerMeter}
                          color={color}
                        />
                      ) : null}
                      <CircleMarker
                        center={[locator.y, locator.x]}
                        radius={isSelf ? 8 : 6}
                        pathOptions={{
                          color: color.stroke,
                          fillColor: color.fill,
                          fillOpacity: 0.9,
                          weight: 2,
                        }}
                      >
                        <Tooltip direction="top" offset={[0, -6]}>
                          {isSelf
                            ? `${entry.display_name} (you) · ${locator.kind === "gps" ? "GPS" : `booth ${entry.booth_id}`}`
                            : `${entry.display_name} · ${locator.kind === "gps" ? "GPS" : `booth ${entry.booth_id}`}`}
                        </Tooltip>
                      </CircleMarker>
                    </Fragment>
                  );
                })}
                {pinOnHall && cadPin && origin ? (
                  <>
                    <GpsAccuracyHalo
                      center={[cadPin.y, cadPin.x]}
                      accuracyM={fix?.accuracyM}
                      unitsPerMeter={origin.unitsPerMeter}
                      color={selfColor}
                    />
                    <CircleMarker
                      center={[cadPin.y, cadPin.x]}
                      radius={8}
                      pathOptions={{ color: "#dce8ff", fillColor: selfColor.fill, fillOpacity: 0.95, weight: 2 }}
                    >
                      <Tooltip direction="top" offset={[0, -6]}>
                        You · GPS
                      </Tooltip>
                    </CircleMarker>
                  </>
                ) : null}
              </FeatureGroup>
            </MapContainer>
            <div className="map-tool-stack">
              {onOpenDrawer ? (
                <button
                  type="button"
                  className="map-control-btn"
                  aria-label="Walk"
                  title={planSummary ? `Walk · ${planSummary}` : "Walk"}
                  onClick={onOpenDrawer}
                >
                  <WalkIcon size={22} />
                </button>
              ) : null}
              {onClearRoute ? (
                <button
                  type="button"
                  className="map-control-btn"
                  aria-label="Clear"
                  title="Clear"
                  onClick={onClearRoute}
                  disabled={!destinationBooth && !(tourPath && tourPath.length >= 2) && !destinationKeys?.size}
                >
                  Clear
                </button>
              ) : null}
              <button
                type="button"
                className="map-control-btn"
                aria-label="Finder"
                title="Finder"
                onClick={() => void handleLocate()}
              >
                <span className="map-locate-icon" aria-hidden="true" />
              </button>
              <button
                ref={alignButtonRef}
                type="button"
                className={`map-control-btn${gpsOn ? " active" : ""}${highlightAlign ? " needs-align" : ""}`}
                aria-pressed={gpsOn}
                aria-label={gpsOn ? "Turn GPS off" : "Turn GPS on"}
                title={gpsStatus}
                onClick={() => void handleGpsToggle()}
                disabled={gpsBusy}
              >
                GPS
              </button>
            </div>
            <div className="map-hall-switcher" role="tablist" aria-label="Hall">
              {HALLS.map((hall) => (
                <button
                  key={hall}
                  type="button"
                  role="tab"
                  aria-selected={hallId === hall}
                  className={`map-control-btn${hallId === hall ? " hall-selected" : ""}`}
                  onClick={() => onHallChange(hall)}
                >
                  {hall}
                </button>
              ))}
            </div>
            <div className="map-team-anchor">
              <TeamBrowser
                presence={presence}
                selfUserId={selfUserId}
                selfEmail={selfEmail}
                selfGpsOn={gpsOn}
                selfOnline={selfOnline}
                showTeamGps={showTeamGps}
                onShowTeamGpsChange={setShowTeamGps}
                onOpenMember={onOpenMember}
              />
            </div>
            {gpsMessage && !gpsOn ? <div className="status hall-map-filter-empty">{gpsMessage}</div> : null}
            {highlightPending ? (
              <div className="status hall-map-filter-empty">Resolving accounts…</div>
            ) : (filtersActive || highlightedBoothIds) && matchCount === 0 ? (
              <div className="status hall-map-filter-empty">No matching booths on this hall.</div>
            ) : null}
          </>
        ) : (
          <div className="status">No booth polygons to plot for this hall.</div>
        )}
      </div>
    </div>
  );
}

type PolygonOrMulti = Polygon | MultiPolygon;
